/**
 * The ball band's recommended range with our own swatch marked on it.
 *
 * The card prints both figures already, two lines apart in the specification block, and
 * a reader has to subtract them in their head to get the one fact that matters: whether
 * the yarn knits where the spinner said it would. This draws it. A quiet span for the
 * band, a brass tick for the measurement, and a sentence underneath that carries every
 * number in the picture, so the drawing is worth nothing to a screen reader and costs it
 * nothing either.
 *
 * ### Why the tick is brass and the band is not
 *
 * Brass carries every figure on this page, and the tick is a figure: it is the only
 * measured number in the drawing. The band is a claim printed on a paper wrapper, so it
 * gets rule ink and sits behind. Sage and rust are not used here at all, in either
 * direction, because on this page those two mean a tool registered or it did not, and a
 * yarn that swatches outside its band has not failed at anything. It is a fact about the
 * yarn, said in words.
 *
 * ### Geometry in the component, colour in the sheet
 *
 * Every x and width below is arithmetic over `gaugePlacement`, and every fill is a class
 * in `gauge.css`. That split is not a preference, it is what `styles/house.test.ts`
 * enforces: no component in this app carries a style object, because a style object is
 * where a colour literal creeps back in. An SVG takes its geometry as attributes, so
 * there is nothing to inline.
 */

import type { YarnRecord } from '../catalogue';

import { gaugePlacement, gaugeStandingText } from './gauge';

/**
 * The drawing's own units. Two hundred wide so a percentage doubles into it and lands on
 * a tenth, eighteen tall which is a shade under the small line height it sits in.
 */
const TRACK_WIDTH = 200;
const TRACK_HEIGHT = 18;
const TICK_WIDTH = 3;

/** Percent along the track to a user unit, kept inside the box at both ends. */
const along = (percent: number): number => (percent / 100) * TRACK_WIDTH;

export interface GaugeBandProps {
  readonly yarn: YarnRecord;
}

export const GaugeBand = ({ yarn }: GaugeBandProps): JSX.Element => {
  const placement = gaugePlacement(yarn);

  const bandX = along(placement.bandStart);
  const bandWidth = Math.max(TICK_WIDTH, along(placement.bandWidth));
  // A swatch on the band's own edge would hang half its tick outside the drawing, and
  // two of the seven yarns on file swatch exactly at their minimum, so this is the
  // ordinary case rather than a guard against a hypothetical one.
  const tickX = Math.min(
    TRACK_WIDTH - TICK_WIDTH,
    Math.max(0, along(placement.swatchAt) - TICK_WIDTH / 2),
  );

  return (
    <figure className="gauge" data-standing={placement.standing}>
      <figcaption className="eyebrow gauge__label">Band against our swatch</figcaption>

      <svg
        aria-hidden="true"
        className="gauge__plot"
        focusable="false"
        viewBox={`0 0 ${TRACK_WIDTH} ${TRACK_HEIGHT}`}
      >
        <rect className="gauge__rail" x="0" y="7" width={TRACK_WIDTH} height="4" rx="2" />
        <rect className="gauge__band" x={bandX} y="4" width={bandWidth} height="10" rx="3" />
        <rect
          className="gauge__tick"
          x={tickX}
          y="0"
          width={TICK_WIDTH}
          height={TRACK_HEIGHT}
          rx="1.5"
        />
      </svg>

      <p className="gauge__read">{gaugeStandingText(yarn)}</p>
    </figure>
  );
};
